import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Megaphone,
  RefreshCw,
  Loader2,
  AlertTriangle,
  CheckCircle,
  Pause,
  Play,
  Euro,
  X,
  Bot,
  FileBarChart,
  LayoutList,
} from "lucide-react";
import apiClient from "@/lib/api-client";
import { extractApiErrorMessage } from "@/lib/api-errors";
import { useGlobalToast } from "@/contexts/ToastContext";
import { useConfirm } from "@/contexts/ConfirmContext";
import GoogleAdsAssistant from "@/components/GoogleAdsAssistant";
import GoogleAdsReports from "@/components/GoogleAdsReports";
import GoogleAdsConversionsCard from "@/components/GoogleAdsConversionsCard";

/**
 * SUPERADMIN → Marketing → Google Ads (FASE 10 de la integración, 13-sep-2026).
 *
 * Lee y gestiona la cuenta REAL de Google Ads vía /api/admin/google-ads/*.
 * Prioriza funcionalidad y seguridad sobre diseño (decisión del plan):
 *  - Activar una campaña SIEMPRE pide confirmación (empieza a gastar).
 *  - Cambiar presupuesto muestra actual → nuevo y confirma; el backend además
 *    aplica el tope GOOGLE_ADS_MAX_DAILY_BUDGET_EUR.
 * La creación visual de campañas vendrá después; el backend ya la soporta.
 *
 * Pestañas "Asistente IA" e "Informes" (sep-2026): la IA propone y el
 * superadmin aplica cada cambio con un clic. Las pestañas inactivas se ocultan
 * sin desmontarse para no perder una respuesta del asistente en curso.
 */

type Tab = "campaigns" | "assistant" | "reports";

const TABS: Array<{ id: Tab; label: string; icon: typeof Bot }> = [
  { id: "campaigns", label: "Campañas", icon: LayoutList },
  { id: "assistant", label: "Asistente IA", icon: Bot },
  { id: "reports", label: "Informes", icon: FileBarChart },
];

interface GoogleAdsStatus {
  configured: boolean;
  code?: string;
  setupFlowEnabled?: boolean;
  customerId?: string;
  accountName?: string;
  currency?: string;
  timeZone?: string;
  apiVersion?: string;
}

interface CampaignRow {
  id: string;
  name: string;
  status: string;
  type: string;
  biddingStrategy: string;
  dailyBudgetEur: number;
  impressions: number;
  clicks: number;
  ctr: number;
  averageCpcEur: number;
  costEur: number;
  conversions: number;
  costPerConversionEur: number;
  conversionsValue: number;
}

const RANGES = [7, 14, 30, 90] as const;

function formatEur(value: number): string {
  return `${value.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    ENABLED: "bg-emerald-50 text-emerald-700 border-emerald-200",
    PAUSED: "bg-amber-50 text-amber-700 border-amber-200",
  };
  const labels: Record<string, string> = { ENABLED: "Activa", PAUSED: "Pausada" };
  return (
    <span
      className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${styles[status] ?? "bg-gray-50 text-gray-600 border-gray-200"}`}
    >
      {labels[status] ?? status}
    </span>
  );
}

export default function GoogleAds() {
  const { showToast } = useGlobalToast();
  const { confirm } = useConfirm();

  const [status, setStatus] = useState<GoogleAdsStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [statusError, setStatusError] = useState<string | null>(null);

  const [days, setDays] = useState<(typeof RANGES)[number]>(30);
  const [campaigns, setCampaigns] = useState<CampaignRow[]>([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [campaignsError, setCampaignsError] = useState<string | null>(null);
  const [actionId, setActionId] = useState<string | null>(null);

  // Modal de presupuesto (actual → nuevo, con confirmación).
  const [budgetTarget, setBudgetTarget] = useState<CampaignRow | null>(null);
  const [budgetValue, setBudgetValue] = useState("");
  const [budgetSaving, setBudgetSaving] = useState(false);

  const [tab, setTab] = useState<Tab>("campaigns");
  const [assistantDraft, setAssistantDraft] = useState<string | null>(null);
  const clearAssistantDraft = useCallback(() => setAssistantDraft(null), []);
  const askAssistant = useCallback((text: string) => {
    setAssistantDraft(text);
    setTab("assistant");
  }, []);

  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    setStatusError(null);
    try {
      const { data } = await apiClient.get<{ success: true; data: GoogleAdsStatus }>(
        "/admin/google-ads/status",
      );
      setStatus(data.data);
    } catch (err) {
      setStatusError(extractApiErrorMessage(err, "No se pudo comprobar la conexión"));
    } finally {
      setStatusLoading(false);
    }
  }, []);

  const loadCampaigns = useCallback(async () => {
    setCampaignsLoading(true);
    setCampaignsError(null);
    try {
      const { data } = await apiClient.get<{ success: true; data: CampaignRow[] }>(
        "/admin/google-ads/campaigns",
        { params: { days } },
      );
      setCampaigns(data.data);
    } catch (err) {
      setCampaignsError(extractApiErrorMessage(err, "No se pudieron cargar las campañas"));
    } finally {
      setCampaignsLoading(false);
    }
  }, [days]);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    if (status?.configured) void loadCampaigns();
  }, [status?.configured, loadCampaigns]);

  // KPIs agregados del rango seleccionado.
  const totals = useMemo(() => {
    const spend = campaigns.reduce((acc, c) => acc + c.costEur, 0);
    const clicks = campaigns.reduce((acc, c) => acc + c.clicks, 0);
    const impressions = campaigns.reduce((acc, c) => acc + c.impressions, 0);
    const conversions = campaigns.reduce((acc, c) => acc + c.conversions, 0);
    return {
      spend,
      impressions,
      clicks,
      conversions,
      cpa: conversions > 0 ? spend / conversions : 0,
      ctr: impressions > 0 ? (clicks / impressions) * 100 : 0,
      cpc: clicks > 0 ? spend / clicks : 0,
    };
  }, [campaigns]);

  const handleToggleStatus = useCallback(
    async (campaign: CampaignRow) => {
      const enabling = campaign.status !== "ENABLED";
      const ok = await confirm({
        title: enabling ? "¿Activar la campaña?" : "¿Pausar la campaña?",
        message: enabling
          ? `"${campaign.name}" empezará a emitir anuncios y a GASTAR (presupuesto: ${formatEur(campaign.dailyBudgetEur)}/día).`
          : `"${campaign.name}" dejará de emitir anuncios.`,
        confirmText: enabling ? "Sí, activar" : "Sí, pausar",
        danger: enabling,
      });
      if (!ok) return;
      setActionId(campaign.id);
      try {
        const { data } = await apiClient.patch<{ success: true; message: string }>(
          `/admin/google-ads/campaigns/${campaign.id}/status`,
          { status: enabling ? "ENABLED" : "PAUSED" },
        );
        showToast(data.message, "success");
        await loadCampaigns();
      } catch (err) {
        showToast(extractApiErrorMessage(err, "No se pudo cambiar el estado"), "error");
      } finally {
        setActionId(null);
      }
    },
    [confirm, showToast, loadCampaigns],
  );

  const handleSaveBudget = useCallback(async () => {
    if (!budgetTarget) return;
    const value = Number(budgetValue.replace(",", "."));
    if (!Number.isFinite(value) || value <= 0) {
      showToast("Introduce un presupuesto válido en euros/día", "error");
      return;
    }
    const ok = await confirm({
      title: "Confirmar cambio de presupuesto",
      message: `"${budgetTarget.name}": de ${formatEur(budgetTarget.dailyBudgetEur)}/día a ${formatEur(value)}/día.`,
      confirmText: "Cambiar presupuesto",
      danger: value > budgetTarget.dailyBudgetEur,
    });
    if (!ok) return;
    setBudgetSaving(true);
    try {
      await apiClient.patch(`/admin/google-ads/campaigns/${budgetTarget.id}/budget`, {
        dailyBudgetEur: value,
      });
      showToast("Presupuesto actualizado", "success");
      setBudgetTarget(null);
      await loadCampaigns();
    } catch (err) {
      showToast(extractApiErrorMessage(err, "No se pudo cambiar el presupuesto"), "error");
    } finally {
      setBudgetSaving(false);
    }
  }, [budgetTarget, budgetValue, confirm, showToast, loadCampaigns]);

  return (
    <div className="space-y-6">
      {/* Cabecera */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Megaphone className="w-6 h-6 text-primary-600" /> Google Ads
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Cuenta publicitaria de ReservaManager — integración directa, sin Windsor.
          </p>
        </div>
        <button
          onClick={() => {
            void loadStatus();
            if (status?.configured) void loadCampaigns();
          }}
          className="inline-flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
        >
          <RefreshCw className="w-4 h-4" /> Actualizar
        </button>
      </div>

      {/* Estado de conexión */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        {statusLoading ? (
          <p className="flex items-center gap-2 text-sm text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Comprobando conexión...
          </p>
        ) : statusError ? (
          <p className="flex items-center gap-2 text-sm text-red-600">
            <AlertTriangle className="w-4 h-4" /> {statusError}
          </p>
        ) : status && !status.configured ? (
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-gray-900">Google Ads no está configurado</p>
              <p className="text-sm text-gray-500 mt-1">
                Faltan variables en Railway. Sigue la guía{" "}
                <code className="bg-gray-100 px-1 rounded">docs/GOOGLE_ADS_INTEGRATION.md</code>{" "}
                del backend
                {status.setupFlowEnabled
                  ? " (el flujo de setup OAuth está activo: GET /api/admin/google-ads/oauth/url)"
                  : ""}
                .
              </p>
            </div>
          </div>
        ) : status ? (
          <div className="flex items-center gap-6 flex-wrap text-sm">
            <span className="inline-flex items-center gap-2 font-medium text-emerald-700">
              <CheckCircle className="w-5 h-5" /> Conectado
            </span>
            <span className="text-gray-600">
              <strong>{status.accountName}</strong> · ID {status.customerId}
            </span>
            <span className="text-gray-500">
              {status.currency} · {status.timeZone} · API {status.apiVersion}
            </span>
          </div>
        ) : null}
      </div>

      {status?.configured && (
        <div className="flex gap-1 border-b border-gray-200 overflow-x-auto">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`inline-flex items-center gap-2 px-3 sm:px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${
                tab === id
                  ? "border-primary-600 text-primary-700"
                  : "border-transparent text-gray-500 hover:text-gray-800"
              }`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>
      )}

      {status?.configured && (
        <div className={tab === "assistant" ? "" : "hidden"}>
          <GoogleAdsAssistant
            draft={assistantDraft}
            onDraftConsumed={clearAssistantDraft}
            onApplied={() => void loadCampaigns()}
          />
        </div>
      )}

      {status?.configured && (
        <div className={tab === "reports" ? "" : "hidden"}>
          <GoogleAdsReports onAskAssistant={askAssistant} />
        </div>
      )}

      {status?.configured && tab === "campaigns" && (
        <>
          {/* Rango + KPIs */}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h2 className="font-semibold text-gray-900">Rendimiento</h2>
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
              {RANGES.map((r) => (
                <button
                  key={r}
                  onClick={() => setDays(r)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    days === r ? "bg-white shadow text-gray-900" : "text-gray-500 hover:text-gray-800"
                  }`}
                >
                  {r} días
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {[
              { label: "Gasto", value: formatEur(totals.spend) },
              { label: "Impresiones", value: totals.impressions.toLocaleString("es-ES") },
              { label: "Clics", value: totals.clicks.toLocaleString("es-ES") },
              { label: "Conversiones", value: totals.conversions.toLocaleString("es-ES") },
              { label: "CPA", value: totals.conversions > 0 ? formatEur(totals.cpa) : "—" },
              { label: "CTR", value: `${totals.ctr.toFixed(2)} %` },
              { label: "CPC medio", value: totals.clicks > 0 ? formatEur(totals.cpc) : "—" },
            ].map((kpi) => (
              <div key={kpi.label} className="bg-white rounded-xl border border-gray-200 p-4">
                <p className="text-xs text-gray-500">{kpi.label}</p>
                <p className="text-lg font-bold text-gray-900 mt-1">{kpi.value}</p>
              </div>
            ))}
          </div>

          {/* Campañas */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {campaignsLoading ? (
              <p className="flex items-center gap-2 text-sm text-gray-500 p-6">
                <Loader2 className="w-4 h-4 animate-spin" /> Cargando campañas...
              </p>
            ) : campaignsError ? (
              <p className="flex items-center gap-2 text-sm text-red-600 p-6">
                <AlertTriangle className="w-4 h-4" /> {campaignsError}
              </p>
            ) : campaigns.length === 0 ? (
              <p className="text-sm text-gray-500 p-6 text-center">
                No hay campañas en la cuenta (excluidas las eliminadas).
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                    <tr>
                      <th className="px-4 py-3">Campaña</th>
                      <th className="px-4 py-3">Estado</th>
                      <th className="px-4 py-3 text-right">Presupuesto/día</th>
                      <th className="px-4 py-3 text-right">Gasto</th>
                      <th className="px-4 py-3 text-right">Clics</th>
                      <th className="px-4 py-3 text-right">Conv.</th>
                      <th className="px-4 py-3 text-right">CPA</th>
                      <th className="px-4 py-3 text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {campaigns.map((campaign) => (
                      <tr key={campaign.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-900">{campaign.name}</p>
                          <p className="text-xs text-gray-400">
                            {campaign.type} · {campaign.biddingStrategy}
                          </p>
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge status={campaign.status} />
                        </td>
                        <td className="px-4 py-3 text-right">{formatEur(campaign.dailyBudgetEur)}</td>
                        <td className="px-4 py-3 text-right">{formatEur(campaign.costEur)}</td>
                        <td className="px-4 py-3 text-right">{campaign.clicks.toLocaleString("es-ES")}</td>
                        <td className="px-4 py-3 text-right">{campaign.conversions}</td>
                        <td className="px-4 py-3 text-right">
                          {campaign.conversions > 0 ? formatEur(campaign.costPerConversionEur) : "—"}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              onClick={() => void handleToggleStatus(campaign)}
                              disabled={actionId === campaign.id}
                              title={campaign.status === "ENABLED" ? "Pausar" : "Activar"}
                              className={`p-1.5 rounded-lg border transition-colors disabled:opacity-50 ${
                                campaign.status === "ENABLED"
                                  ? "border-amber-200 text-amber-600 hover:bg-amber-50"
                                  : "border-emerald-200 text-emerald-600 hover:bg-emerald-50"
                              }`}
                            >
                              {actionId === campaign.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : campaign.status === "ENABLED" ? (
                                <Pause className="w-4 h-4" />
                              ) : (
                                <Play className="w-4 h-4" />
                              )}
                            </button>
                            <button
                              onClick={() => {
                                setBudgetTarget(campaign);
                                setBudgetValue(String(campaign.dailyBudgetEur));
                              }}
                              title="Modificar presupuesto"
                              className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50"
                            >
                              <Euro className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <GoogleAdsConversionsCard />
        </>
      )}

      {/* Modal de presupuesto */}
      {budgetTarget && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Modificar presupuesto</h3>
              <button
                onClick={() => setBudgetTarget(null)}
                className="p-1 rounded hover:bg-gray-100 text-gray-400"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-1">{budgetTarget.name}</p>
            <p className="text-sm text-gray-500 mb-4">
              Presupuesto actual: <strong>{formatEur(budgetTarget.dailyBudgetEur)}/día</strong>
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nuevo presupuesto (€/día)
            </label>
            <input
              type="number"
              min="1"
              step="0.5"
              value={budgetValue}
              onChange={(e) => setBudgetValue(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-400 mt-2">
              El backend aplica el tope de seguridad configurado (100 €/día por defecto).
            </p>
            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setBudgetTarget(null)}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Cancelar
              </button>
              <button
                onClick={() => void handleSaveBudget()}
                disabled={budgetSaving}
                className="px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {budgetSaving ? "Guardando..." : "Continuar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
